export class NADProductMobileHeader {
  private element: HTMLElement;
  private variantEl: HTMLElement | null;
  private priceEl: HTMLElement | null;
  private compareEl: HTMLElement | null;
  private dotEl: HTMLElement | null;
  private priceObserver: MutationObserver | null = null;
  private productInfoObserver: MutationObserver | null = null;
  private observedPriceContainer: Element | null = null;
  private productInfoSyncTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(element: HTMLElement) {
    this.element = element;
    this.variantEl = element.querySelector('[data-mobile-header-variant]');
    this.priceEl = element.querySelector('[data-mobile-header-price]');
    this.compareEl = element.querySelector('[data-mobile-header-compare]');
    this.dotEl = element.querySelector('.nad-product-mobile-header__dot');
    this.init();
  }

  private init(): void {
    this.listenVariantChanges();
    this.observePriceContainer();
    this.observeProductInfo();
  }

  private listenVariantChanges(): void {
    const poll = (retries = 30): void => {
      const input = document.querySelector<HTMLInputElement>(
        'input[name="id"].product-variant-id'
      );
      if (input) {
        input.addEventListener('change', () => this.syncFromDOM());
        return;
      }
      if (retries > 0) setTimeout(() => poll(retries - 1), 100);
    };
    poll();

    document.addEventListener('product-info:loaded', () => {
      this.reattachPriceObserver();
      this.syncFromDOM();
    });
  }

  private observePriceContainer(): void {
    const poll = (retries = 40): void => {
      const container = document.querySelector("[id^='price-']");
      if (container) {
        this.watchPriceContainer(container);
        this.syncFromDOM();
        return;
      }
      if (retries > 0) setTimeout(() => poll(retries - 1), 200);
    };
    poll();
  }

  private watchPriceContainer(container: Element): void {
    if (this.observedPriceContainer === container) return;
    this.priceObserver?.disconnect();
    this.observedPriceContainer = container;
    this.priceObserver = new MutationObserver(() => {
      this.syncPrice();
    });
    this.priceObserver.observe(container, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  private reattachPriceObserver(): void {
    const container = document.querySelector("[id^='price-']");
    if (container && container !== this.observedPriceContainer) {
      this.watchPriceContainer(container);
    }
  }

  private observeProductInfo(): void {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;
    this.productInfoObserver = new MutationObserver(() => {
      if (this.productInfoSyncTimer != null) clearTimeout(this.productInfoSyncTimer);
      this.productInfoSyncTimer = setTimeout(() => {
        this.productInfoSyncTimer = null;
        this.reattachPriceObserver();
        this.syncPrice();
      }, 120);
    });
    this.productInfoObserver.observe(productInfo, {
      childList: true,
      subtree: true,
    });
  }

  private syncFromDOM(): void {
    setTimeout(() => {
      this.syncVariantLabel();
      this.syncPrice();
    }, 80);
  }

  private syncVariantLabel(): void {
    if (!this.variantEl) return;

    const script = document.querySelector('variant-selects [data-selected-variant]');
    if (!script?.textContent) return;

    try {
      const variant = JSON.parse(script.textContent);
      const isDefault = !variant.title || variant.title === 'Default Title';

      if (isDefault) {
        this.variantEl.style.display = 'none';
        if (this.dotEl) this.dotEl.style.display = 'none';
      } else {
        this.variantEl.textContent = variant.title;
        this.variantEl.style.display = '';
        if (this.dotEl) this.dotEl.style.display = '';
      }
    } catch {
      // ignore
    }
  }

  private syncPrice(): void {
    if (this.syncPriceFromContainer()) return;
    this.syncPriceFromVariantData();
  }

  private syncPriceFromContainer(): boolean {
    const container = document.querySelector("[id^='price-']");
    if (!container) return false;

    const isOnSale = container.classList.contains('price--on-sale');

    if (isOnSale) {
      this.element.classList.add('nad-product-mobile-header--on-sale');
      const sale = container.querySelector('.price__sale');
      if (sale) {
        const salePrice = sale.querySelector(
          '.price-item--sale, .price-item--last'
        ) as HTMLElement | null;
        const comparePrice = sale.querySelector(
          's.price-item--regular'
        ) as HTMLElement | null;

        if (salePrice && this.priceEl) {
          this.priceEl.textContent = salePrice.textContent?.trim() || '';
        }
        if (comparePrice && this.compareEl) {
          this.compareEl.textContent = comparePrice.textContent?.trim() || '';
          this.compareEl.style.display = '';
        }
      }
    } else {
      this.element.classList.remove('nad-product-mobile-header--on-sale');
      const regular = container.querySelector('.price__regular');
      if (regular) {
        const price = regular.querySelector(
          '.price-item--regular'
        ) as HTMLElement | null;
        if (price && this.priceEl) {
          this.priceEl.textContent = price.textContent?.trim() || '';
        }
      }
      if (this.compareEl) this.compareEl.style.display = 'none';
    }
    return true;
  }

  private syncPriceFromVariantData(): void {
    if (!this.priceEl) return;

    const script = document.querySelector('variant-selects [data-selected-variant]');
    if (!script?.textContent) return;

    try {
      const variant = JSON.parse(script.textContent);
      if (!variant.price) return;

      const formatted = this.formatMoney(variant.price);
      if (!formatted) return;

      this.priceEl.textContent = formatted;

      const isOnSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (isOnSale) {
        this.element.classList.add('nad-product-mobile-header--on-sale');
        if (this.compareEl) {
          this.compareEl.textContent = this.formatMoney(variant.compare_at_price) || '';
          this.compareEl.style.display = '';
        }
      } else {
        this.element.classList.remove('nad-product-mobile-header--on-sale');
        if (this.compareEl) this.compareEl.style.display = 'none';
      }
    } catch {
      // ignore
    }
  }

  private formatMoney(cents: number): string | null {
    const w = window as any;
    if (w.Shopify && typeof w.Shopify.formatMoney === 'function') {
      const format = w.Shopify.money_format || w.Shopify.moneyFormat || '{{amount}}';
      return w.Shopify.formatMoney(cents, format);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  public destroy(): void {
    this.priceObserver?.disconnect();
    this.productInfoObserver?.disconnect();
  }

  static initializeAll(): void {
    document
      .querySelectorAll<HTMLElement>('#NadProductMobileHeader')
      .forEach((el) => {
        if (!(el as any)._nadComponentInstance) {
          (el as any)._nadComponentInstance = new NADProductMobileHeader(el);
        }
      });
  }
}
